<template>
    <div :id="selector" class="block blank double-padded">

        <the-header v-if="!settings.hide_header" :settings="settings" :certificate="certificate"></the-header>

        <div class="s-page-body">
            <div class="s-table-box space-20">
                <div class="content" v-if="!hideAll">
                    <div class="giftcertificate-attention-warning" v-if="certificate.is_expired">
                        {{ $t('Certificate expired') }}
                    </div>
                    <div class="giftcertificate-attention-info" v-if="certificate.id">
                        <p>
                            {{ $t('The certificate was created at {date}', { date: certificate.create_datetime }) }}
                            <span class="inline" v-if="certificate.order_id > 0">
                                {{ $t('from the order') }}
                                <a v-if="typeof settings.orders[certificate.order_id] !== 'undefined'"
                                   :href="settings.backend_url + `shop/?action=orders#/orders/id=${certificate.order_id}/`"
                                >
                                    {{ settings.orders[certificate.order_id].id_encoded }}
                                    <i v-if="settings.orders[certificate.order_id].icon" :class="settings.orders[certificate.order_id].icon"></i>
                                </a>
                                <span v-else># {{ certificate.order_id }}</span>
                            </span>
                            <span v-if="certificate.create_contact_id > 0">
                                {{ $t('by user') }}
                                <a :href="settings.backend_url + 'contacts/#/contact/' + certificate.create_contact_id">
                                    {{ getContactName(certificate.create_contact_id) }}
                                </a>
                            </span>
                        </p>
                        <p v-if="certificate.contact_id && certificate.contact_id.id !== undefined">
                            {{ $t('Only available for user') }}
                            <a :href="settings.backend_url + 'contacts/#/contact/' + certificate.contact_id.id" target="_blank">
                                {{ getContactName(certificate.contact_id.id) }}
                                <base-icon16 v-if="!settings.is_frontend" :id="`new-window`"></base-icon16>
                                <i v-else class="gc-icon new-window"></i>
                            </a>
                        </p>
                        <p v-if="!certificate.is_expired && certificate.lifetime_expired">
                            {{ $t('Expired') }}: {{ certificate.lifetime_expired }}. {{ $tc('Left {$n} lifetime days', certificate.lifetime_left) }}
                        </p>
                        <div v-if="certificate.orders.length > 0"
                             :class="{'giftcertificate-attention-warning' : certificate.usage.type !== 'multiple'}"
                        >
                            <div class="certificate-orders">
                                <div>{{ $t('Certificate was used in the orders:') }}</div>
                                <div v-for="(order_id, index) in certificate.orders" :key="index">
                                <span class="shop-tooltip" v-if="order_id < 0 && typeof settings.orders[(-1)*order_id] !== 'undefined'">
                                    <base-icon16 :id="`clock`"></base-icon16>
                                    <span>{{ $t('Order is not paid') }}</span>
                                </span>
                                    <a v-if="typeof settings.orders[Math.abs(order_id)] !== 'undefined'"
                                       :href="settings.backend_url + `shop/?action=orders#/orders/id=${Math.abs(order_id)}/`"
                                    >
                                        {{ settings.orders[Math.abs(order_id)].id_encoded }}
                                        <i v-if="settings.orders[Math.abs(order_id)].icon" :class="settings.orders[Math.abs(order_id)].icon"></i>
                                    </a>
                                    <span v-else># {{ Math.abs(order_id) }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="fields width100">
                        <div class="field" v-if="certificate.code || settings.states">
                            <!-- Code field -->
                            <wa-field :name="$t('Code')" v-if="certificate.code">
                                <span class="gray-block">{{ certificate.code }}</span>
                            </wa-field>
                            <!-- States field -->
                            <wa-field :name="$t('State')" v-if="settings.states">
                                <select v-model="certificate.state_id">
                                    <option value="0">{{ $t('Select the state') }}</option>
                                    <option v-for="(state, index) in settings.states"
                                            :key="index"
                                            :value="state.id"
                                    >{{ state.name }}
                                    </option>
                                </select>
                            </wa-field>
                        </div>

                        <div v-for="(field, findex) in fields" :key="findex"
                             v-if="((settings.is_frontend && parseInt(field.status)) || !settings.is_frontend) && (field.system_code || field.id)"
                        >
                            <!-- HTML field -->
                            <wa-field v-if="settings.is_frontend && field.type === 'html'"
                                      :name="field.name"
                                      :hide_name="field.name === ''"
                                      :class="field.css_class">
                                <div v-html="field.value"></div>
                            </wa-field>

                            <!-- To field, From field, Message field, Custom field -->
                            <wa-field v-if="field.type === 'input:text' || field.type === 'textarea'"
                                      :name="settings.is_frontend ? field.name : field.system_name"
                                      :hide_name="settings.is_frontend && field.name === ''"
                                      :hide_name_class ="`width100`"
                                      :class="{
                                          required: settings.is_frontend && parseInt(field.required),
                                          'hide-name': settings.is_frontend && field.name === '',
                                          'field-error': $v.certificate[field.id] !== undefined && $v.certificate[field.id].$error,
                                      }"
                            >
                                <div v-if="field.text_before && field.text_before !== '' && settings.is_frontend" v-html="field.text_before"></div>
                                <input v-if="field.type === 'input:text'"
                                       type="text"
                                       v-model="certificate[field.id]"
                                       :placeholder="field.placeholder"
                                       :class="{ [field.css_class]: settings.is_frontend }"
                                >
                                <base-textarea v-if="field.type === 'textarea'"
                                               v-model="certificate.message"
                                               :locale="settings.locale"
                                               :redactor="false"
                                ></base-textarea>
                                <div class="error" v-if="$v.certificate[field.id] !== undefined && hasError($v.certificate[field.id], 'required')">{{ $t('Field is required') }}</div>
                                <div v-if="field.text_after && field.text_after !== '' && settings.is_frontend" v-html="field.text_after"></div>
                            </wa-field>

                            <!-- Amount field -->
                            <wa-field v-if="field.system_code === 'amount'"
                                      :name="settings.is_frontend ? field.name : field.system_name"
                                      :hide_name="settings.is_frontend && field.name === ''"
                                      :class="{ required: !certificate.products.length || settings.system_settings.min_sum > 0, 'field-error': $v.certificate.amount.$error }"
                            >
                                <div v-if="field.text_before && field.text_before !== '' && settings.is_frontend" v-html="field.text_before"></div>
                                <input type="text" class="width100px"
                                       v-model="certificate.amount"
                                       @input="$v.certificate.amount.$touch"
                                       :class="{ [field.css_class]: settings.is_frontend }"
                                >
                                <select v-model="certificate.currency" class="inherit">
                                    <option value="%" v-if="!settings.is_frontend && !settings.is_backend_order_edit">%</option>
                                    <option v-for="(currency, index) in settings.currencies" :key="index"
                                            :value="currency.code"
                                    >{{ currency.sign }} {{ currency.code }}
                                    </option>
                                </select>
                                <div v-if="settings.amount_hints"
                                     class="mb"
                                >
                                    <a v-for="(hint, index) in settings.amount_hints" :key="index"
                                       class="inline-link mb right"
                                       @click="certificate.amount=hint.value"
                                    ><b><i>{{ hint.name }}</i></b></a>
                                </div>
                                <div class="error" v-if="hasError($v.certificate.amount, 'required')">{{ $t('Field is required') }}</div>
                                <div class="error" v-if="hasError($v.certificate.amount, 'between')">
                                    <span v-if="serverErrors.amount && serverErrors.amount.message">{{ serverErrors.amount.message[0] }}</span>
                                    <span v-else>{{
                                            $t('Amount should be between {min} and {max}', {
                                                min: $v.certificate.amount.$params.between.min,
                                                max: $v.certificate.amount.$params.between.max
                                            })
                                        }}</span>
                                </div>
                                <div v-if="field.text_after && field.text_after !== '' && settings.is_frontend" v-html="field.text_after"></div>
                            </wa-field>
                            <!-- Type of sending field -->
                            <div v-if="field.system_code === 'send' && ((settings.is_frontend && Object.keys(field.variants).length) || !settings.is_frontend)">
                                <wa-field v-if="Object.keys(field.variants).length > 1"
                                          :name="settings.is_frontend ? field.name : field.system_name"
                                          :hide_name="settings.is_frontend && field.name === ''"
                                >
                                    <div v-for="(variant, variantId) in field.variants" :key="`variant`+variantId" class="mb bottom">
                                        <label :for="`variant`+variantId">
                                            <input type="radio" name="send"
                                                   :id="`variant`+variantId"
                                                   :value="variant.type"
                                                   v-model="certificate.send"
                                                   :class="field.css_class"
                                            > {{ variant.name }}
                                        </label>
                                    </div>
                                </wa-field>
                                <div v-for="(variant, variantId2) in field.variants" :key="variantId2">
                                    <!-- Address block -->
                                    <wa-field v-if="certificate.send === 'post' && variant.type === 'post'"
                                              :name="settings.is_frontend ? variant.name : $t('Address')"
                                              :hide_name="settings.is_frontend && variant.name === ''"
                                              class="address-block"
                                    >
                                        <div class="mb bottom" v-if="settings.is_frontend" v-html="field.before_address_text"></div>
                                        <!-- Country field -->
                                        <div class="address-item" :class="{ 'is-loading': loadingStatus.regions, required: true, 'field-error': $v.certificate.post.country.$error }">
                                            <label class="small grey">{{ $t('Country') }}</label>
                                            <select v-model="certificate.post.country" @change="loadRegions(); $v.certificate.post.country.$touch;" :disabled="loadingStatus.regions">
                                                <option value="">{{ $t('Select country') }}</option>
                                                <option v-for="(country, index) in countries" :key="index" :value="country.iso3letter">{{ country.name }}</option>
                                            </select>
                                            <div class="error" v-if="hasError($v.certificate.post.country, 'required')">{{ $t('Field is required') }}</div>
                                        </div>
                                        <!-- Region field -->
                                        <div v-if="isCountryHasRegions(certificate.post.country)"
                                             class="address-item" :class="{ 'is-loading': loadingStatus.regions, required: true, 'field-error': $v.certificate.post.region.$error }"
                                        >
                                            <label class="small grey">{{ $t('Region') }}</label>
                                            <select v-model="certificate.post.region" @change="$v.certificate.post.region.$touch" :disabled="loadingStatus.regions">
                                                <option value="">{{ $t('Select region') }}</option>
                                                <option v-for="(region, index) in regions[certificate.post.country]"
                                                        :key="index"
                                                        :value="region.code"
                                                >{{ region.name }}
                                                </option>
                                            </select>
                                            <div class="error" v-if="hasError($v.certificate.post.region, 'required')">{{ $t('Field is required') }}</div>
                                        </div>
                                        <!-- City field -->
                                        <div class="address-item width100" :class="{ required: true, 'field-error': $v.certificate.post.city.$error }">
                                            <label class="small grey">{{ $t('City') }}</label>
                                            <input type="text" v-model="certificate.post.city" @input="$v.certificate.post.city.$touch">
                                            <div class="error" v-if="hasError($v.certificate.post.city, 'required')">{{ $t('Field is required') }}</div>
                                        </div>
                                        <!-- Address field -->
                                        <div class="address-item" :class="{ required: true, 'field-error': $v.certificate.post.street.$error }">
                                            <label class="small grey">{{ $t('Street address') }}</label>
                                            <input type="text" v-model="certificate.post.street" @input="$v.certificate.post.street.$touch">
                                            <div class="error" v-if="hasError($v.certificate.post.street, 'required')">{{ $t('Field is required') }}</div>
                                        </div>
                                        <!-- Postcode field -->
                                        <div class="address-item" :class="{ required: true, 'field-error': $v.certificate.post.postcode.$error }">
                                            <label class="small grey">{{ $t('Postcode') }}</label>
                                            <input type="text" v-model="certificate.post.postcode" @input="$v.certificate.post.postcode.$touch">
                                            <div class="error" v-if="hasError($v.certificate.post.postcode, 'required')">{{ $t('Field is required') }}</div>
                                        </div>
                                        <!-- Phone field -->
                                        <div class="address-item" :class="{ required: true, 'field-error': $v.certificate.post.phone.$error }">
                                            <label class="small grey">{{ $t('Phone') }}</label>
                                            <input type="text" v-model="certificate.post.phone" @input="$v.certificate.post.phone.$touch">
                                            <div class="error" v-if="hasError($v.certificate.post.phone, 'required')">{{ $t('Field is required') }}</div>
                                        </div>
                                    </wa-field>
                                    <!-- Email field -->
                                    <wa-field v-else-if="certificate.send === 'email' && variant.type === 'email'"
                                              :name="settings.is_frontend ? variant.name : $t('Email')"
                                              :hide_name="settings.is_frontend && variant.name === ''"
                                              :hide_name_class ="`width100`"
                                              :class="{
                                                  required: true,
                                                  'hide-name': settings.is_frontend && field.name === '',
                                                  'field-error': $v.certificate.email.$error
                                              }"
                                    >
                                        <input type="text" v-model="certificate.email" @input="$v.certificate.email.$touch">
                                        <div class="error" v-if="hasError($v.certificate.email, 'required')">{{ $t('Field is required') }}</div>
                                        <div class="error" v-if="hasError($v.certificate.email, 'email')">{{ $t('Incorrect email') }}</div>
                                    </wa-field>
                                    <!-- User field -->
                                    <wa-field v-else-if="certificate.send === 'user' && !settings.is_frontend && variant.type === 'user'" :name="$t('User')" :class="{ required: true, 'field-error': $v.certificate.contact_id.$error }">
                                        <v-autocomplete :items="autocompleteUserItems"
                                                        v-model="certificate.contact_id"
                                                        :get-label="(item) => { if (item) { return item.name; } }"
                                                        :min-len="2"
                                                        :component-item='autocompleteTemplate'
                                                        :auto-select-one-item="false"
                                                        @update-items="updateAutocompleteItems"
                                                        :input-attrs="{
                                                class: 'width100',
                                                placeholder: $t('Start typing user name or email')
                                            }"
                                                        :class="{ 'is-loading': loadingStatus.autocomplete }"
                                        ></v-autocomplete>
                                        <div class="error" v-if="hasError($v.certificate.contact_id, 'required')">{{ $t('Field is required') }}</div>
                                        <div class="error" v-if="hasError($v.certificate.contact_id, 'contact')">
                                            <span v-if="serverErrors.contact_id && serverErrors.contact_id.message.length">
                                                {{ serverErrors.contact_id.message.join(', ') }}
                                            </span>
                                            <span v-else>{{ $t('Select the correct contact') }}</span>
                                        </div>
                                        <div class="mb top">
                                            {{ $t('Certificate will be available only for selected user') }}
                                        </div>
                                    </wa-field>
                                </div>
                            </div>

                            <!-- Free products for frontend -->
                            <the-free-products v-if="field.system_code === 'products' && settings.is_frontend"
                                               :settings="settings"
                                               :certificate="certificate"
                                               :field="field"
                                               :class="{ [field.css_class]: settings.is_frontend }"
                            ></the-free-products>
                        </div>
                        <!-- Limitations -->
                        <wa-field :name="$t('Use limitations')" v-if="!settings.is_frontend">
                            <base-checkbox v-model="certificate.use_limitations" :checked="isUseLimitations"></base-checkbox>
                        </wa-field>
                        <div v-if="certificate.use_limitations && !settings.is_frontend">
                            <wa-field :name="$t('Expired')" class="mb top double">
                                <input type="checkbox" class="mb right"
                                       v-if="certificate.id"
                                       v-model="disabled.expired"
                                       :checked="!disabled.expired" :value="true" :true-value="false" :false-value="true"
                                >
                                <base-datepicker v-model="certificate.limitations.expired" :locale="settings.locale" :disabled="disabled.expired"></base-datepicker>
                            </wa-field>
                            <wa-field :name="$t('Lifetime')">
                                <input type="checkbox" class="mb right"
                                       v-if="certificate.id"
                                       v-model="disabled.lifetime"
                                       :checked="!disabled.lifetime" :value="true" :true-value="false" :false-value="true"
                                >
                                <input type="number" v-model.number="certificate.limitations.lifetime" :disabled="disabled.lifetime"> {{ $tc('{$n} days', certificate.limitations.lifetime) }}
                                <div class="gray mb top">{{ $t('Number of days the certificate will be active after creating.') }}</div>
                            </wa-field>
                            <wa-field :name="$t('Maximal sum, which can be spent')">
                                <input type="number" v-model.number="certificate.limitations.max"> % {{ $t('from total price of order without shipping price') }}
                            </wa-field>
                            <wa-field :name="$t('Minimal order sum')">
                                <input type="number" v-model.number="certificate.limitations.min.value">
                                <select v-model="certificate.limitations.min.currency" class="inherit">
                                    <option v-for="(currency, index) in settings.currencies" :key="index"
                                            :value="currency.code"
                                    >{{ currency.sign }} {{ currency.code }}
                                    </option>
                                </select>
                                <div class="gray mb top">{{ $t('Minimal total price of order, from which certificate can be applied.') }}</div>
                            </wa-field>
                        </div>
                        <!-- Number of usage -->
                        <wa-field :name="$t('Number of usage')" v-if="!settings.is_frontend">
                            <div class="mb bottom">
                                <label>
                                    <input type="radio" v-model="certificate.usage.type" name="usage_type" value="disposable">
                                    {{ $t('disposable') }}
                                </label>
                                <div class="gray">{{ $t('The certificate will be disposable.') }}</div>
                            </div>
                            <div class="mb bottom">
                                <label>
                                    <input type="radio" v-model="certificate.usage.type" name="usage_type" value="multiple">
                                    {{ $t('multiple') }}
                                </label>
                                <div class="gray">{{ $t('The certificate can be used multiple times.') }}</div>
                            </div>
                        </wa-field>
                    </div>
                    <div v-if="certificate.id">
                        <hr/>
                        <wa-field :name="$t('Certificate template')">
                            <div class="mb bottom">
                                <input type="radio" name="template_type" class="mb right" value="default" v-model="certificate.template_type">
                                <select v-model="certificate.default_template" @click="certificate.template_type='default'">
                                    <option v-for="(template, index) in settings.system_settings.templates"
                                            :key="index"
                                            :value="index">
                                        {{ template.name }}
                                    </option>
                                </select>
                            </div>
                            <label @click="certificate.template_type='custom'">
                                <input type="radio" name="template_type" value="custom" class="mb right" v-model="certificate.template_type">
                                <base-modal :text="$t('edit the template of the current certificate')"
                                            :title="$t('Editing certificate template')"
                                            :component="editTemplateComponent"
                                            :componentParams="{
                                            id: 'common',
                                            url: this.settings.urls.get_template,
                                            postData: {
                                              id: certificate.id
                                            },
                                            revertLink: {
                                                title: $t('revert to default template'),
                                                url: this.settings.urls.revert_template,
                                                template_id: certificate.default_template
                                            }
                                        }"
                                            :saveData.sync="certificate"
                                            :buttons="{
                                            submit: {
                                                id: 'edit-template-submit',
                                                name: $t('Save'),
                                                saveUrl:  this.settings.urls.save_template,
                                                onPreSave: updateEditor
                                            }
                                        }"
                                            :waitComponentLoaded="true"
                                >&nbsp;
                                </base-modal>
                            </label>
                        </wa-field>
                    </div>

                    <!-- Free products for backend -->
                    <the-free-products v-if="!settings.is_frontend"
                                       :settings="settings"
                                       :certificate="certificate"
                                       :field="{}"
                    ></the-free-products>

                    <hr/>
                    <div class="field clear-both" v-if="(certificate.send === 'email' || certificate.send === 'user') && !settings.is_backend_order_edit && !settings.is_frontend">
                        <base-checkbox v-model="certificate.send_notification"
                                       :checked="parseInt(certificate.send_notification)"
                                       :label="$t('Send notification to person who the certificate is intended for')"
                        ></base-checkbox>
                    </div>

                    <div class="clear-both error-block" v-if="typeof serverErrors.common !== 'undefined'">
                        <div v-for="(error, index) in serverErrors.common.message" :key="index">
                            {{ error }}
                        </div>
                    </div>

                    <div class="mb top double clear-both">
                        <base-submit-block :saveUrl="this.settings.urls.save_certificate + (!this.settings.is_frontend ? (settings.order_id || 0) : '')"
                                           :button="$t('Save')"
                                           :data.sync="certificate"
                                           :onValidate="validateDataBeforeSubmit"
                                           :options="{
                                               successStatus: (!certificate.id && !settings.is_backend_order && !settings.is_backend_order_edit) ? 1 : 2,
                                               hideErrors: true
                                           }"
                                           @pre-save="serverErrors={}"
                                           @saved="onSaved"
                        ></base-submit-block>
                        <div v-if="certificate.id" class="align-right">
                            <base-modal :text="`<i class='icon16 delete'></i> ` + $t('remove certificate')"
                                        :title="$t('Are you sure?')"
                                        :saveData="[certificate.id]"
                                        :buttons="{
                                            submit: {
                                                name: $t('Delete'),
                                                saveUrl: this.settings.urls.remove_certificate,
                                                options: {
                                                    statesText: {
                                                        reloading: $t('Removed successfully. Page is reloading') + '...',
                                                    },
                                                    buttonClass: 'red',
                                                    reloadOnSuccess: true,
                                                    redirectUrl: settings.app_url + (settings.is_old_ss ? `?plugin=giftcertificates` : `marketing/giftcertificates/`),
                                                    successStatus: 1
                                                },
                                            },
                                        }"
                            >{{ $t('Do you really want to remove the certificate?') }}
                            </base-modal>
                        </div>
                    </div>
                </div>
                <div v-else-if="Object.keys(responseOnHideAll).length">
                    <div v-if="settings.is_backend_order_edit">
                        <p class="is-loading mb left">{{ $t('Wait, please, certificate is loading') }}</p>
                    </div>
                    <div v-else-if="settings.is_frontend" class="successfull-message">
                        <div v-if="responseOnHideAll.message" v-html="responseOnHideAll.message"></div>
                        <p v-else>{{ $t('The certificate was successfully created and added to the cart.') }}</p>
                    </div>
                    <div v-else>
                        <p>
                            <a :href="settings.app_url + (settings.is_old_ss ? `?plugin=giftcertificates&id=` : `marketing/giftcertificates/`) + responseOnHideAll.id">{{ $t('Certificate') }}</a> {{ $t('was created successfully.') }}
                        </p>
                        <p>{{ $t('Code:') }} <b>{{ responseOnHideAll.code }}</b></p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import qs from "qs";
import BaseEditTemplate from "@/components/base/BaseEditTemplate";
import TheAutocompleteTemplate from "./TheAutocompleteTemplate";
import {validationMixin} from 'vuelidate';
import {required, email, between} from 'vuelidate/lib/validators';
import ProductSearchModule from "@/components/products_search/store";

export default {
    components: {
        'base-textarea': () => import('@/components/base/BaseTextarea'),
        'base-modal': () => import('@/components/base/BaseModal'),
        'base-datepicker': () => import('@/components/base/BaseDatepicker'),
        'base-icon16': () => import('@/components/base/BaseIcon16'),
        'base-checkbox': () => import('@/components/base/BaseCheckbox'),
        'base-submit-block': () => import('@/components/base/BaseSubmitBlock'),
        'wa-field': () => import('@/components/WaField'),
        'the-header': () => import('./TheHeader'),
        'the-free-products': () => import('./TheFreeProducts'),
        'draggable': () => import('vuedraggable'),
        'v-autocomplete': () => import('v-autocomplete')
    },
    mixins: [validationMixin],
    props: ['selector', 'settings'],
    data() {
        return {
            fields: [],
            certificate: {
                post: {
                    country: '',
                    region: '',
                },
                amount: 0,
                currency: '',
                send: 'email',
                send_notification: 0,
                products: [],
                state_id: 0,
                use_limitations: false,
                template: '',
                default_template: '',
                template_type: 'default',
                limitations: {
                    expired: '',
                    lifetime: '',
                    max: '',
                    min: {
                        value: '',
                        currency: ''
                    }
                },
                usage: {
                    type: 'disposable'
                }
            },
            currency: '$',
            countries: [],
            regions: {},
            states_list: [],
            loadingStatus: {
                regions: false,
                autocomplete: false
            },
            serverErrors: {},
            editTemplateComponent: null,
            disabled: {
                expired: false,
                lifetime: false
            },
            hideAll: false,
            responseOnHideAll: {},
            autocompleteUserItems: [],
            autocompleteTemplate: null
        }
    },
    computed: {
        isUseLimitations() {
            return !!(!this.settings.is_frontend && (this.certificate.limitations.expired || this.certificate.limitations.lifetime || this.certificate.limitations.min.value || this.certificate.limitations.max))
        }
    },
    methods: {
        getContactName(contactId) {
            if (typeof this.settings.contacts[contactId] !== 'undefined') {
                return this.settings.contacts[contactId].name;
            } else {
                return 'ID #' + contactId;
            }
        },
        validateDataBeforeSubmit() {
            this.$v.$touch();
            if (this.$v.$invalid) {
                return this.$t('Fix the errors above');
            }
            return true;
        },
        serverValidator(validatorName, field, defaultValidatorResult) {
            return (typeof this.serverErrors[field] === 'undefined' || this.serverErrors[field].validators.indexOf(validatorName) === -1) && defaultValidatorResult;
        },
        hasError(field, validatorName) {
            return field.$error && !field[validatorName];
        },
        onSaved(response) {
            if (response.data.status === 'fail' && response.data.errors && response.data.errors.length) {
                response.data.errors.forEach((error) => {
                    const key = error[1]['field'];
                    if (typeof this.serverErrors[key] === 'undefined') {
                        this.$set(this.serverErrors, key, { message: [], validators: [] });
                    }
                    this.serverErrors[key].message.push(error[0]);
                    this.serverErrors[key].validators.push(error[1]['validator']);
                });
                this.$v.$touch();
            } else if (response.data.status === 'ok' && !this.certificate.id) {
                if (this.settings.is_backend_order || this.settings.is_backend_order_edit || this.settings.is_frontend) {
                    this.hideAll = true;
                    this.responseOnHideAll = response.data.data;
                    this.$emit('saved', response.data);
                } else {
                    location.href = this.settings.app_url + (this.settings.is_old_ss ? `?plugin=giftcertificates&id=` : `marketing/giftcertificates/`) + response.data.data.id;
                }
            }
        },
        updateEditor() {
            this.certificate['template'] = $('#textarea-editor-common').data('wa_editor').getValue();
        },
        /**
         * Load regions for country iso3letter
         */
        loadRegions() {
            const self = this;
            if (typeof self.regions[self.certificate.post.country] === 'undefined' && !self.loadingStatus.regions) {
                self.loadingStatus.regions = true;

                self.$http.post(this.settings.urls.load_regions,
                    qs.stringify({ _csrf: self.csrf, iso3letter: self.certificate.post.country }),
                    { headers: { 'content-type': 'application/x-www-form-urlencoded' } }
                ).then(function (response) {
                    self.regions[self.certificate.post.country] = response.data.data;
                }).catch(function (error) {
                    self.regions[self.certificate.post.country] = [];
                    console && console.warn(error);
                }).finally(function () {
                    self.loadingStatus.regions = false;
                });
            }
        },
        isCountryHasRegions(country) {
            return typeof this.regions[country] !== 'undefined' && this.regions[country].length;
        },
        updateAutocompleteItems(searchText) {
            const self = this;
            if (!self.loadingStatus.autocomplete) {
                self.loadingStatus.autocomplete = true;

                self.$http.post(this.settings.urls.contact_autocomplete,
                    qs.stringify({ _csrf: self.csrf, text: searchText }),
                    { headers: { 'content-type': 'application/x-www-form-urlencoded' } }
                ).then(function (response) {
                    self.autocompleteUserItems = response.data.data;
                }).catch(function (error) {
                    self.autocompleteUserItems = [];
                    console && console.warn(error);
                }).finally(function () {
                    self.loadingStatus.autocomplete = false;
                });
            }
        },
    },
    validations() {
        let validationParams = {
            certificate: {
                amount: {
                    required: function (value) {
                        if (!this.certificate.products.length || this.settings.system_settings.min_sum > 0) {
                            return this.serverValidator('required', 'amount', required(value));
                        }
                        return true;
                    },
                    between: function (value) {
                        let result = true;
                        return this.serverValidator('between', 'amount', this.certificate.currency === '%' ? between(0, 100)(value) : result);
                    }
                },
                email: {
                    required: function (value) {
                        if (this.certificate.send === 'email') {
                            return this.serverValidator('required', 'email', required(value));
                        }
                        return true;
                    },
                    email: function (value) {
                        if (this.certificate.send === 'email') {
                            return this.serverValidator('email', 'email', email(value));
                        }
                        return true;
                    },
                },
                contact_id: {
                    required: function (value) {
                        if (this.certificate.send === 'user') {
                            return this.serverValidator('required', 'contact_id', required(value));
                        }
                        return true;
                    },
                    contact: function () {
                        if (this.certificate.send === 'user') {
                            let result = true;
                            return this.serverValidator('contact', 'contact_id', result);
                        }
                        return true;
                    },
                },
                post: {
                    country: {
                        required: function (value) {
                            if (this.certificate.send === 'post') {
                                return this.serverValidator('required', 'post.country', required(value));
                            }
                            return true;
                        },
                    },
                    region: {
                        required: function (value) {
                            if (this.certificate.send === 'post' && this.isCountryHasRegions(this.certificate.post.country)) {
                                return this.serverValidator('required', 'post.region', required(value));
                            }
                            return true;
                        },
                    },
                    city: {
                        required: function (value) {
                            if (this.certificate.send === 'post') {
                                return this.serverValidator('required', 'post.city', required(value));
                            }
                            return true;
                        },
                    },
                    street: {
                        required: function (value) {
                            if (this.certificate.send === 'post') {
                                return this.serverValidator('required', 'post.street', required(value));
                            }
                            return true;
                        },
                    },
                    postcode: {
                        required: function (value) {
                            if (this.certificate.send === 'post') {
                                return this.serverValidator('required', 'post.postcode', required(value));
                            }
                            return true;
                        },
                    },
                    phone: {
                        required: function (value) {
                            if (this.certificate.send === 'post') {
                                return this.serverValidator('required', 'post.phone', required(value));
                            }
                            return true;
                        },
                    },
                }
            },
        }
        if (this.settings.is_frontend) {
            // Set custom required fields
            this.fields.forEach((field) => {
                if (parseInt(field.required)) {
                    validationParams.certificate[field.id] = {
                        required: function (value) {
                            return this.serverValidator('required', field.id, required(value));
                        },
                    }
                }
            });
        }
        return validationParams;
    },
    created() {
        if (!this.settings.is_frontend) {
            this.certificate = Object.assign(this.certificate, this.settings.certificate);
        }
        if (!this.certificate.currency) {
            this.certificate.currency = this.settings.currency_info.code;
        }
        this.countries = this.settings.countries;
        this.editTemplateComponent = BaseEditTemplate;
        this.autocompleteTemplate = TheAutocompleteTemplate;

        if (!this.$store.hasModule('ProductSearchModule')) {
            this.$store.registerModule('ProductSearchModule', ProductSearchModule);
        }

        if (this.certificate.products.length) {
            this.$store.commit('ProductSearchModule/update', this.certificate.products);
        }

        if (this.certificate.id && this.isUseLimitations) {
            this.disabled.expired = true;
            this.disabled.lifetime = true;
        } else if (!this.certificate.id) {
            this.certificate.limitations = Object.assign({}, this.settings.system_settings.limitations);
        }
        this.certificate.use_limitations = this.isUseLimitations;

        this.fields = this.settings.system_settings.fields;

        if (!this.certificate.id && this.certificate.amount <= 0 && this.settings.system_settings.min_sum > 0) {
            this.certificate.amount = parseFloat(this.settings.system_settings.min_sum)
        }

        if (this.settings.is_frontend || (typeof this.certificate.contact_id !== 'object' && !parseInt(this.certificate.contact_id))) {
            this.certificate.contact_id = undefined;
        }
        if (!this.settings.is_frontend) {
            if (parseInt(this.certificate.contact_id)) {
                this.certificate.contact_id = {
                    id: parseInt(this.certificate.contact_id),
                    name: this.getContactName(this.certificate.contact_id)
                };
            }
            if (this.certificate.template_is_changed) {
                this.certificate.template_type = 'custom';
            }
            if (!this.certificate.default_template) {
                this.certificate.default_template = this.settings.system_settings.default_template;
            }
        } else {
            if (this.settings.certificate) {
                for (const [key, field] of Object.entries(this.settings.certificate)) {
                    this.$set(this.certificate, key, field);
                }
            }
        }

        this.loadRegions();

        $?.shop?.marketing?.setTitle(this.certificate.id ? this.$t('Certificate') + ': ' + this.certificate.code : this.$t('New certificate'));
    },
}
</script>
<style>
.sort { cursor: move; }
.s-page-header { position: relative; }
.error-block { padding: 10px; border: 2px solid #f00; color: #f00; text-align: center; margin-top: 20px; line-height: 1.5; }
.free-products { margin-bottom: 1.5em; }
.free-products .product-item { display: flex; margin-bottom: 10px; }
.free-products .product-item > span { padding: 0 2px; }
.free-products .product-item-price { font-weight: 600; padding: 0 5px; }
.field-error input, .field-error select { border-color: #f00; color: #f00; }
.error { color: #f00; margin-top: 5px; font-size: .9em; }
.v-autocomplete { position: relative }
.v-autocomplete .v-autocomplete-list { position: absolute; z-index: 999; width: 100%; text-align: left; max-height: 400px; overflow-y: auto; border: 1px solid #ccc; box-shadow: 0 0 5px #aaa; }
.v-autocomplete .v-autocomplete-list .v-autocomplete-list-item { cursor: pointer; background-color: #fff; padding: 10px; }
.v-autocomplete .v-autocomplete-list .v-autocomplete-list-item:hover { background-color: #eee; }
.v-autocomplete .v-autocomplete-list .v-autocomplete-list-item.v-autocomplete-item-active { background-color: #f3f6fa; }

i.gc-icon { background-color: #666; height: 12px; width: 12px; display: inline-block; text-indent: -9999px; margin: 0 3px; text-decoration: none !important; }
i.gc-icon.new-window { mask-image: url("data:image/svg+xml,%3C%3Fxml version='1.0'%3F%3E%3Csvg version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' x='0px' y='0px' viewBox='0 0 26 26' style='enable-background:new 0 0 26 26;' xml:space='preserve'%3E%3Cg%3E%3Cpath style='fill:%23030104;' d='M18,17.759v3.366C18,22.159,17.159,23,16.125,23H4.875C3.841,23,3,22.159,3,21.125V9.875 C3,8.841,3.841,8,4.875,8h3.429l3.001-3h-6.43C2.182,5,0,7.182,0,9.875v11.25C0,23.818,2.182,26,4.875,26h11.25 C18.818,26,21,23.818,21,21.125v-6.367L18,17.759z'/%3E%3Cpath style='fill:%23030104;' d='M22.581,0H12.322c-1.886,0.002-1.755,0.51-0.76,1.504l3.22,3.22l-5.52,5.519 c-1.145,1.144-1.144,2.998,0,4.141l2.41,2.411c1.144,1.141,2.996,1.142,4.14-0.001l5.52-5.52l3.16,3.16 c1.101,1.1,1.507,1.129,1.507-0.757L26,3.419C25.999-0.018,26.024-0.001,22.581,0z'/%3E%3C/g%3E%3C/svg%3E%0A"); }
</style>